import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/passwordInput';
import { Icon } from '@/components/common/Icon';
import { showSuccessNotification } from '@/utils/notifications';
import { useAuth } from '@/hooks/useAuth';
import { useRequestEmailChangeMutation } from '@/hooks/useAccountSecurity';

export function ChangeEmailForm() {
  const { t } = useTranslation('settings');
  const { refreshCurrentUser } = useAuth();
  const requestEmailChangeMutation = useRequestEmailChangeMutation();
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  function openForm() {
    setErrorMessage('');
    setNewEmail('');
    setCurrentPassword('');
    setIsChangingEmail(true);
  }

  function cancelForm() {
    setIsChangingEmail(false);
    setErrorMessage('');
  }

  async function submitEmailChange(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setErrorMessage('');
    const requestedEmail = newEmail.trim();
    try {
      await requestEmailChangeMutation.mutateAsync({ currentPassword, newEmail: requestedEmail });
      await refreshCurrentUser();
      showSuccessNotification(t('practitioner.contact.changeEmailSent', { email: requestedEmail }));
      setIsChangingEmail(false);
      setNewEmail('');
      setCurrentPassword('');
    } catch (changeError) {
      const backendMessage = resolveApiErrorMessage(changeError);
      setErrorMessage(
        backendMessage.length > 0 ? backendMessage : t('practitioner.contact.changeEmailError')
      );
    }
  }

  if (!isChangingEmail) {
    return (
      <Button type="button" variant="ghost" size="small" onClick={openForm}>
        {t('practitioner.contact.changeEmailButton')}
      </Button>
    );
  }

  return (
    <form onSubmit={submitEmailChange} className="mt-2 space-y-3 rounded-xl border border-zooborder p-4">
      <p className="text-sm font-semibold text-zooindigo">
        {t('practitioner.contact.changeEmailHeading')}
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="contact-new-email">{t('practitioner.contact.newEmailLabel')}</Label>
        <Input
          id="contact-new-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={newEmail}
          onChange={function updateNewEmail(changeEvent) {
            setNewEmail(changeEvent.target.value);
          }}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="contact-email-password">
          {t('practitioner.contact.currentPasswordLabel')}
        </Label>
        <PasswordInput
          id="contact-email-password"
          required
          autoComplete="current-password"
          value={currentPassword}
          onChange={function updateCurrentPassword(changeEvent) {
            setCurrentPassword(changeEvent.target.value);
          }}
        />
      </div>

      {errorMessage.length > 0 ? (
        <div
          role="alert"
          className="flex items-center gap-2 px-3.5 py-2.5 bg-zooerrorbg border border-zooerrorborder rounded-xl text-xs font-medium text-zooerrortext"
        >
          <Icon name="AlertCircle" />
          {errorMessage}
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <Button type="submit" size="small" isLoading={requestEmailChangeMutation.isPending}>
          {t('practitioner.contact.changeEmailSubmit')}
        </Button>
        <Button type="button" variant="ghost" size="small" onClick={cancelForm}>
          {t('practitioner.contact.changeEmailCancel')}
        </Button>
      </div>
    </form>
  );
}
